/* tslint:disable:forbid-import */
// Because this decorator applies hotkeys outside of render, we need to
// manually hook into mobx to support hot-swapping of keysets.
import { autorun } from 'mobx';
/* tslint:enable:forbid-import */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { lazyMousetrapImport } from '../utils/lazyMousetrapImport';
import { HotkeyCommand, Hotkeys } from '../types/HotkeyCommand';
import UseKeydownHandlerOptions from '../types/UseKeydownHandlerOptions';
import bindEventWithMousetrap from '../utils/bindEventWithMousetrap';

export interface KeydownConfig {
    command: HotkeyCommand | Hotkeys;
    handler: (evt?: React.KeyboardEvent) => void;
    options?: UseKeydownHandlerOptions;
}

export function lazyKeydown<
    TProps extends {},
    TState extends {},
    TComponent extends React.Component<TProps, TState, any>
>(
    attach: (target?: TComponent) => Promise<KeydownConfig[]>,
    dispose?: (target?: TComponent) => Promise<void> | void
) {
    return function<TComponentClass extends React.ComponentClass<TProps, TState>>(
        target: TComponentClass
    ): TComponentClass {
        let mousetrapInstance, autorunDisposer;
        let onMount = target.prototype.componentDidMount;
        let componentMounted;
        target.prototype.componentDidMount = function() {
            componentMounted = true;
            if (onMount) {
                onMount.call(this, arguments);
            }

            Promise.all([lazyMousetrapImport.import(), attach(this)]).then(
                ([Mousetrap, configArray]) => {
                    // ReactDOM.findDomNode can throw an exception if the component is not mounted.
                    // No need to add the key down handler if the component is already unmounted
                    // https://reactjs.org/docs/react-dom.html#finddomnode
                    let domNode: Element | undefined;
                    try {
                        domNode =
                            componentMounted &&
                            (ReactDOM.findDOMNode(this) as (Element | undefined));
                    } catch {
                        // supress the error;
                    }

                    if (domNode) {
                        mousetrapInstance = new Mousetrap(domNode);
                        autorunDisposer = autorun(() => {
                            mousetrapInstance.reset();
                            configArray.forEach(keydownConfig => {
                                bindEventWithMousetrap(
                                    this,
                                    mousetrapInstance,
                                    keydownConfig.command,
                                    keydownConfig.handler,
                                    keydownConfig.options
                                );
                            });
                        });
                    }
                }
            );
        };

        let onUnmount = target.prototype.componentWillUnmount;
        target.prototype.componentWillUnmount = function() {
            componentMounted = false;
            if (onUnmount) {
                onUnmount.call(this, arguments);
            }

            mousetrapInstance && mousetrapInstance.reset();
            autorunDisposer && autorunDisposer();
            dispose && dispose(this);
        };

        return target;
    };
}
